import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProgressBar } from "@/components/ProgressBar";
import { HealthBadge, PriorityBadge } from "@/components/Badges";
import { formatWIB } from "@/lib/format";
import { ArchiveButton } from "@/components/ArchiveButton";
import { Rocket, Plus, Target as TargetIcon, GitBranch, Layers } from "lucide-react";
import { toast } from "sonner";

const toIso = (v) => (v ? new Date(v + ":00+07:00").toISOString() : null);

function MissionDetail({ mid, open, onOpenChange, onChanged, divisions }) {
  const [m, setM] = useState(null);
  const [showStrat, setShowStrat] = useState(false);
  const [sTitle, setSTitle] = useState("");
  const [sApproach, setSApproach] = useState("");
  const [sDeadline, setSDeadline] = useState("");
  const load = () => api.get(`/missions/${mid}`).then((r) => setM(r.data));
  useEffect(() => { if (open && mid) load(); }, [open, mid]);
  const stratRef = useRef(null);
  useEffect(() => { if (showStrat) stratRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [showStrat]);
  if (!m) return <Sheet open={open} onOpenChange={onOpenChange}><SheetContent className="w-full sm:max-w-xl" aria-describedby={undefined}><SheetHeader className="sr-only"><SheetTitle>Memuat detail misi</SheetTitle></SheetHeader><div className="p-6 text-slate-400">Memuat...</div></SheetContent></Sheet>;

  const addStrategy = async () => {
    if (!sTitle.trim()) return toast.error("Judul strategi wajib");
    await api.post("/strategies", { mission_id: mid, title: sTitle, approach: sApproach, deadline: toIso(sDeadline), start_date: new Date().toISOString() });
    toast.success("Strategi ditambahkan"); setSTitle(""); setSApproach(""); setSDeadline(""); setShowStrat(false); load(); onChanged?.();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto zoa-scroll" data-testid="mission-detail" aria-describedby={undefined}>
        <SheetHeader><SheetTitle className="text-left">{m.title}</SheetTitle></SheetHeader>
        <div className="flex items-center gap-2 mt-2"><PriorityBadge priority={m.priority} /><HealthBadge health={m.health} />{m.division && <span className="text-xs font-semibold" style={{ color: m.division.color }}>{m.division.name}</span>}</div>
        <p className="text-sm text-slate-600 mt-3">{m.background}</p>
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-1"><span className="font-semibold">Progres Roll-up</span><span className="font-bold text-indigo-600">{m.progress}%</span></div>
          <ProgressBar value={m.progress} />
          <p className="text-xs text-slate-400 mt-1">Deadline: {formatWIB(m.deadline, false)}</p>
        </div>
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1"><Layers className="h-3.5 w-3.5" />Strategi ({m.strategies?.length || 0})</p>
            <Button size="sm" variant="outline" onClick={() => setShowStrat((v) => !v)} data-testid="add-strategy-btn"><Plus className="h-4 w-4 mr-1" />Strategi</Button>
          </div>
          {showStrat && (
            <div ref={stratRef} className="rounded-xl border border-slate-200 p-3 mb-3 space-y-2">
              <Input placeholder="Judul strategi" value={sTitle} onChange={(e) => setSTitle(e.target.value)} data-testid="strategy-title-input" />
              <Textarea placeholder="Pendekatan" value={sApproach} onChange={(e) => setSApproach(e.target.value)} rows={2} />
              <Input type="datetime-local" value={sDeadline} onChange={(e) => setSDeadline(e.target.value)} />
              <Button size="sm" onClick={addStrategy} data-testid="strategy-save-btn">Simpan</Button>
            </div>
          )}
          <div className="space-y-2">
            {m.strategies?.map((s) => (
              <div key={s.id} className="rounded-xl border border-slate-200 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-700 text-sm">{s.title}</span>
                  <div className="flex items-center gap-2"><HealthBadge health={s.health} /><span className="text-xs font-bold text-slate-500">{s.progress}%</span></div>
                </div>
                <ProgressBar value={s.progress} className="h-1.5 mt-2" />
                <p className="text-xs text-slate-400 mt-1">{s.task_count} tugas · deadline {formatWIB(s.deadline, false)}</p>
              </div>
            ))}
            {(!m.strategies || m.strategies.length === 0) && <p className="text-sm text-slate-400 italic">Belum ada strategi.</p>}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Missions() {
  const { user } = useAuth();
  const [missions, setMissions] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: "", background: "", division_id: "", priority: "tinggi", deadline: "" });
  const canCreate = ["super_admin", "owner", "atasan", "kepala_divisi"].includes(user?.role);

  const load = () => api.get("/missions").then((r) => setMissions(r.data));
  useEffect(() => { load(); api.get("/divisions").then((r) => setDivisions(r.data)); }, []);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Judul wajib");
    await api.post("/missions", { ...form, deadline: toIso(form.deadline), start_date: new Date().toISOString() });
    toast.success("Misi dibuat"); setCreateOpen(false); setForm({ title: "", background: "", division_id: "", priority: "tinggi", deadline: "" }); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><Rocket className="h-7 w-7 text-indigo-500" />Misi & Strategi</h1>
          <p className="text-slate-500 text-sm">Peta top-down: Misi → Strategi → Tugas dengan roll-up otomatis</p>
        </div>
        {canCreate && <Button onClick={() => setCreateOpen(true)} data-testid="create-mission-btn" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1.5" />Buat Misi</Button>}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {missions.map((m) => (
          <div key={m.id} onClick={() => { setOpenId(m.id); setDrawer(true); }} data-testid={`mission-card-${m.id}`}
            className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:-translate-y-0.5 hover:shadow-lg transition-all">
            <div className="flex items-start justify-between gap-2">
              <p className="font-bold text-slate-800 leading-snug">{m.title}</p>
              <PriorityBadge priority={m.priority} />
            </div>
            <p className="text-xs text-slate-400 mt-1 line-clamp-2">{m.background}</p>
            <div className="flex items-center justify-between text-xs mt-3 mb-1">
              <span className="text-slate-500">Progres <b className="text-slate-700">{m.progress}%</b></span><HealthBadge health={m.health} />
            </div>
            <ProgressBar value={m.progress} />
            <div className="flex items-center justify-between text-xs text-slate-400 mt-3 pt-2 border-t border-slate-100">
              <span className="inline-flex items-center gap-1"><GitBranch className="h-3 w-3" />{m.strategy_count} strategi</span>
              <div className="flex items-center gap-1">
                {m.division && <span className="font-semibold" style={{ color: m.division.color }}>{m.division.name}</span>}
                {canCreate && <ArchiveButton url={`/missions/${m.id}`} label="misi ini" onDone={load} testid={`archive-mission-${m.id}`} />}
              </div>
            </div>
          </div>
        ))}
        {missions.length === 0 && <p className="text-slate-400 col-span-full text-center py-12">Belum ada misi.</p>}
      </div>

      <MissionDetail mid={openId} open={drawer} onOpenChange={setDrawer} onChanged={load} divisions={divisions} />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Buat Misi Baru</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Judul</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="mission-title-input" /></div>
            <div><Label>Latar Belakang</Label><Textarea value={form.background} onChange={(e) => setForm((f) => ({ ...f, background: e.target.value }))} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Divisi</Label>
                <Select value={form.division_id} onValueChange={(v) => setForm((f) => ({ ...f, division_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{divisions.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Prioritas</Label>
                <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="kritis">Kritis</SelectItem><SelectItem value="tinggi">Tinggi</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="rendah">Rendah</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Deadline</Label><Input type="datetime-local" value={form.deadline} onChange={(e) => setForm((f) => ({ ...f, deadline: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Batal</Button><Button onClick={create} data-testid="mission-create-submit" className="bg-indigo-600 hover:bg-indigo-700">Buat</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
